import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { FullSession } from "../types"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"

import { useAIParserSession } from "./use-ai-parser-session"

type ImportTemplateData = {
  templateId: string
  templateTitle: string
}

function importTemplate(data: ImportTemplateData): Promise<FullSession> {
  // Create a new parser session with template columns pre-loaded
  return api.post("/create-parser-session", {
    custom_title: data.templateTitle,
    template_id: data.templateId,
  })
}

export function useImportTemplateMutation() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setCurrentSessionId, sessionType } = useAIParserSession()

  return useMutation({
    mutationFn: (data: ImportTemplateData) => importTemplate(data),
    onSuccess: (response) => {
      setCurrentSessionId(response.id)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(response.id),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })
      toast.success(t("pages.ai_parser.toasts.template_imported_success"))

      void navigate(`/ai-parser/chat/${response.id}`)
    },
    onError: () => {
      toast.error(t("pages.ai_parser.toasts.template_import_failed"))
    },
  })
}
